import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from './components/NavigationBar';
import { AuthContext } from './AuthContext';
import { notificationStyles } from './utils';
import { groupSetupKey } from './totpEnrollment';

const Enable2FA = () => {
  const navigate = useNavigate();
  const [qrCode, setQrCode] = useState(null);
  const [manualKey, setManualKey] = useState('');
  const [accountLabel, setAccountLabel] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const { axiosInstance, setToken } = useContext(AuthContext);
  const groupedKey = useMemo(() => groupSetupKey(manualKey), [manualKey]);

  const setupHeaders = () => {
    const setupToken = localStorage.getItem('temp_token');
    if (!setupToken) throw new Error('setup_session_missing');
    return { Authorization: `Bearer ${setupToken}` };
  };

  useEffect(() => {
    let active = true;
    const loadEnrollment = async () => {
      try {
        const response = await axiosInstance.post('/api/2fa/enrollment', {}, { headers: setupHeaders() });
        if (!active) return;
        setQrCode(response.data.totp_qr_code);
        setManualKey(response.data.manual_setup_key);
        setAccountLabel(response.data.account_label || '');
      } catch (error) {
        if (!active) return;
        const code = error.response?.data?.detail?.code || error.message;
        setNotification({
          type: 'error',
          message: code === 'setup_session_missing' || String(code).includes('setup_session')
            ? 'Your secure setup session expired. Sign in again to continue 2FA setup.'
            : 'Pinngro could not load 2FA setup. Sign in again and retry.',
        });
      } finally {
        if (active) setLoading(false);
      }
    };
    loadEnrollment();
    return () => { active = false; };
  }, [axiosInstance]);

  const copySetupKey = async () => {
    try {
      await navigator.clipboard.writeText(manualKey);
      setCopied(true);
      setNotification({ type: 'success', message: 'Setup key copied.' });
    } catch {
      setNotification({ type: 'error', message: 'Copy was unavailable. Select the setup key and copy it manually.' });
    }
  };

  const completeEnrollment = async (event) => {
    event.preventDefault();
    setLoading(true);
    setNotification(null);
    try {
      const response = await axiosInstance.post(
        '/api/2fa/enrollment/confirm',
        { totp_code: totpCode.trim() },
        { headers: setupHeaders() },
      );
      const { access_token: accessToken, is_onboarded: isOnboarded } = response.data;
      if (!accessToken) throw new Error('missing_application_session');
      setQrCode(null);
      setManualKey('');
      setAccountLabel('');
      localStorage.removeItem('temp_token');
      localStorage.removeItem('pending2FASetup');
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('token', accessToken);
      axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      setToken(accessToken);
      navigate(isOnboarded ? '/dashboard' : '/onboarding', { replace: true });
    } catch (error) {
      const code = error.response?.data?.detail?.code;
      setNotification({
        type: 'error',
        message: code === 'invalid_enrollment_code'
          ? 'That code did not match. Check the current 6-digit code and try again.'
          : code?.includes('setup_session')
            ? 'Your secure setup session expired. Sign in again to continue.'
            : '2FA setup could not be completed. Nothing was confirmed; please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <NavigationBar />
      <main className="mx-auto flex min-h-screen w-full max-w-2xl items-center px-4 pb-12 pt-24 sm:px-6">
        <section className="w-full overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-800 dark:bg-slate-900 sm:p-8">
          <p className="text-sm font-bold uppercase tracking-[0.16em] text-blue-600">Account security</p>
          <h1 className="mt-2 text-3xl font-black">Set up two-factor authentication</h1>
          <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">2FA is required before Pinngro can grant application access. Use any authenticator app that supports time-based setup.</p>
          {notification && <div className="mt-5 rounded-xl border p-4" style={notificationStyles[notification.type]}>{notification.message}</div>}
          {qrCode && manualKey ? (
            <form className="mt-6 space-y-6" onSubmit={completeEnrollment}>
              <div className="text-center"><img src={qrCode} alt="Two-factor authentication QR code" className="mx-auto max-w-[200px]" /></div>
              <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/30">
                <h2 className="font-black">Can’t scan the QR code?</h2>
                <p className="mt-2 text-sm">Using this phone? Enter the setup key manually in your authenticator app.</p>
                <p className="mt-4 text-sm font-bold">Enter this setup key manually in your authenticator app:</p>
                <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <code className="min-w-0 flex-1 break-all rounded-xl bg-white px-4 py-3 text-sm font-black tracking-wider text-slate-900 dark:bg-slate-950 dark:text-white" aria-label="Manual authenticator setup key">{groupedKey}</code>
                  <button type="button" onClick={copySetupKey} className="shrink-0 rounded-xl border border-blue-400 px-4 py-3 text-sm font-black text-blue-700 hover:bg-blue-100 dark:text-blue-300">{copied ? 'COPIED' : 'COPY SETUP KEY'}</button>
                </div>
                <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
                  <div><dt className="font-bold">Issuer</dt><dd>Pinngro</dd></div>
                  <div><dt className="font-bold">Type</dt><dd>Time-based</dd></div>
                  {accountLabel && <div className="sm:col-span-2"><dt className="font-bold">Account</dt><dd className="break-all">{accountLabel}</dd></div>}
                </dl>
              </div>
              <label className="block text-sm font-bold">
                Enter the 6-digit code generated by your authenticator app to finish setup.
                <input value={totpCode} onChange={(event) => setTotpCode(event.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required className="mt-3 block w-full rounded-xl border border-slate-300 bg-transparent px-4 py-3 text-lg tracking-[0.35em] dark:border-slate-700" aria-label="Six-digit authenticator code" />
              </label>
              <button type="submit" disabled={loading || totpCode.length !== 6} className="w-full rounded-xl bg-blue-600 px-5 py-3 font-black text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">{loading ? 'Checking code…' : 'Complete 2FA setup'}</button>
              <p className="text-xs leading-5 text-slate-500">Leaving this page does not grant access. Sign in again at any time to resume this secure setup.</p>
            </form>
          ) : loading ? <p className="mt-6 text-sm text-slate-600">Preparing secure 2FA setup…</p> : <button type="button" onClick={() => navigate('/login', { replace: true })} className="mt-6 w-full rounded-xl bg-blue-600 px-5 py-3 font-black text-white">Return to sign in</button>}
        </section>
      </main>
    </div>
  );
};

export default Enable2FA;
